// Revise's additions to the Project menu, and the sheets they open (rooms/menus.ts; README.md ▸ Menus), in the Mac's
// groups: Revisions (Start Revision…, or the marks, Next and Previous Change, Start New Revision…, Accept All and
// Reject All), Check Style, and Next and Previous Finding while Checks is shown. The keys work wherever the project is
// open: ⌥⌘] and ⌥⌘[ move between the changes in Write, ⇧⌘L checks the manuscript's style, and ⌘' and ⇧⌘' step through
// Checks' findings (Ctrl stands for ⌘ off the Mac).
import { createElement } from 'react';
import type { MenuContext, RoomMenu } from '../menus.ts';
import { navigate } from '../../shell/route.ts';
import { findingStepper, revisionsOf, showsRevisionMarks } from '../../store/revise.ts';
import { revisionControls } from '../../store/reviseEditor.ts';
import { revisionEntries, RevisionConfirm, StartRevisionSheet, type RevisionSheet } from '../../views/revise/Revisions.tsx';

const SHEETS: Record<RevisionSheet, string> = { start: 'revise/start-revision', acceptAll: 'revise/accept-all', rejectAll: 'revise/reject-all' };

export const menu: RoomMenu = {
  items: (ctx: MenuContext) => {
    const active = revisionsOf(ctx.project).revisions.length > 0;
    const controls = revisionControls();
    return [
      { kind: 'heading', label: 'Revisions' },
      ...revisionEntries({
        active,
        shown: showsRevisionMarks(),
        count: controls?.changeCount ?? 0,
        canWrite: ctx.canWrite,
        reveal: controls ? (forward) => controls.reveal(forward) : null,
        open: (sheet) => ctx.openRoomSheet(SHEETS[sheet]),
      }),
      { kind: 'separator' },
      { label: 'Check Style', shortcut: '⇧⌘L', onSelect: () => ctx.navigate(ctx.hrefFor('revise/checks', null, { check: '1' })) },
      // Only while Checks is shown: the menu is long enough already.
      ...(findingStepper()
        ? [
            { label: 'Next Finding', shortcut: "⌘'", onSelect: () => findingStepper()?.(true) },
            { label: 'Previous Finding', shortcut: "⇧⌘'", onSelect: () => findingStepper()?.(false) },
          ]
        : []),
    ];
  },
  sheets: {
    'revise/start-revision': (ctx) => createElement(StartRevisionSheet, { project: ctx.project, live: ctx.live, onClose: ctx.onClose }),
    'revise/accept-all': (ctx) => createElement(RevisionConfirm, { action: 'acceptAll', project: ctx.project, live: ctx.live, onClose: ctx.onClose }),
    'revise/reject-all': (ctx) => createElement(RevisionConfirm, { action: 'rejectAll', project: ctx.project, live: ctx.live, onClose: ctx.onClose }),
  },
};

/** ⌥⌘] / ⌥⌘[ (Next / Previous Change), ⇧⌘L (Check Style) and ⌘' / ⇧⌘' (Next / Previous Finding), wherever a project
 *  is open. */
function keys(e: KeyboardEvent) {
  const mod = e.metaKey || e.ctrlKey;
  if (!mod || e.getModifierState?.('AltGraph')) return;
  if (e.altKey && !e.shiftKey && (e.code === 'BracketRight' || e.code === 'BracketLeft')) {
    const controls = revisionControls();
    if (!controls) return;
    e.preventDefault();
    controls.reveal(e.code === 'BracketRight');
  } else if (e.shiftKey && !e.altKey && e.code === 'KeyL') {
    const m = /^\/p\/([^/]+)/.exec(location.pathname);
    if (!m) return;
    e.preventDefault();
    navigate(`/p/${m[1]}/revise/checks?check=1`);
  } else if (!e.altKey && e.code === 'Quote') {
    const step = findingStepper();
    if (!step) return;
    e.preventDefault();
    step(!e.shiftKey);
  }
}

if (typeof window !== 'undefined') window.addEventListener('keydown', keys);
